import { parseTableAttributeValue, serialiseTableAttributeValue, type GeneratedNodeAttribute } from "@keydials/api-client";

/**
 * A Table attribute (KEY-568) offers rows of data against columns the author wrote out at definition time.
 * This resolves what the grid should show for one field on one node — kept pure, and out of the panel, for
 * the same reason {@link listAttributeState} is: the interesting rule is which data survives, and that
 * needs no DOM to be checked.
 *
 * Reuses List's own "checked on the way in, never on the way out" stance: a value shaped for a column set
 * that has since been re-authored is kept and flagged rather than silently dropped, exactly as a List's
 * off-list value is — only here there is no single "current answer" to flag, so the whole table is flagged
 * malformed instead.
 */
export interface TableAttributeState {
  /** The declared column headers, in author order. Empty when the author hasn't added any yet. */
  columns: string[];
  /** The saved rows, each one cell per column, in column order. Empty when unset. */
  rows: string[][];
  /** The stored value doesn't parse against the CURRENT columns — a re-authored column set, or corrupt
   *  data. Never true merely because there are no rows yet. */
  malformed: boolean;
}

/** Resolves what a Table field should show. */
export function tableAttributeState(
  decl: Pick<GeneratedNodeAttribute, "options">,
  value: string,
): TableAttributeState {
  const columns = (decl.options ?? []).map((o) => o.trim()).filter((o) => o.length > 0);
  const rows = parseTableAttributeValue(value.trim(), columns.length);
  return { columns, rows: rows ?? [], malformed: rows === null };
}

/** Serialises a table's rows for save; an empty table serialises to "" so the server clears it. */
export function serialiseTableRows(rows: readonly (readonly string[])[]): string {
  return serialiseTableAttributeValue(rows);
}
